package er.prateek.crossly.ui.screens.math

import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.backhandler.BackHandler
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.key.utf16CodePoint
import androidx.compose.ui.unit.dp
import androidx.lifecycle.Lifecycle
import androidx.lifecycle.LifecycleEventObserver
import androidx.lifecycle.compose.LocalLifecycleOwner
import er.prateek.crossly.ads.AdPlacement
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.math.SavedMathSession
import er.prateek.crossly.data.model.GameMode
import er.prateek.crossly.progression.recordCompletionAndLog
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.game.math.MathCrosswordEngine
import er.prateek.crossly.generation.math.MathCrosswordGenerator
import er.prateek.crossly.ui.coach.reportCoachBounds
import er.prateek.crossly.ui.components.GameButton
import er.prateek.crossly.ui.components.GameButtonStyle
import er.prateek.crossly.ui.components.GameScreen
import er.prateek.crossly.ui.components.GameTopBar
import er.prateek.crossly.ui.components.IconBulb
import er.prateek.crossly.ui.components.IconCheck
import er.prateek.crossly.ui.components.NumberPad
import er.prateek.crossly.ui.components.StatChip
import er.prateek.crossly.ui.screens.crossword.LetterDragOverlay
import er.prateek.crossly.ui.screens.crossword.rememberLetterDragState
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import kotlin.time.TimeSource
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

private val MODE = GameMode.MATH_CROSSWORD.analyticsName

@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun MathCrosswordScreen(
    levelId: String,
    isDaily: Boolean = false,
    onBack: () -> Unit,
    onSolved: (levelId: String, stars: Int, elapsedMs: Long) -> Unit,
) {
    val tier = remember(levelId) {
        runCatching { DifficultyTier.valueOf(levelId.split("-")[1].uppercase()) }.getOrDefault(DifficultyTier.EASY)
    }
    val index = remember(levelId) { levelId.substringAfterLast("-").toIntOrNull() ?: 1 }
    val mathConfig = AppContainer.appConfig().modes.mathCrossword

    // A matching saved session means resume; anything else means a fresh, config-driven generate.
    val savedForLevel = remember(levelId) { AppContainer.mathSessionStore.load()?.takeIf { it.levelId == levelId } }
    val resumed = savedForLevel != null
    val puzzle = remember(levelId) {
        if (savedForLevel != null) {
            savedForLevel.puzzle
        } else {
            AppContainer.mathSessionStore.save(null) // drop any stale session for another level
            val nonce = AppContainer.mathSessionStore.nextNonce()
            val cfg = AppContainer.appConfig().modes.mathCrossword
            MathCrosswordGenerator.generateForLevel(
                levelId = levelId,
                tier = tier,
                index = index,
                nonce = nonce,
                options = MathCrosswordGenerator.Options(
                    blankRatioOverride = cfg.blankRatio,
                    allowMultiply = cfg.allowMultiply,
                    allowDivide = cfg.allowDivide,
                ),
                levelsPerTier = cfg.levelsPerTier,
            )
        }
    }
    val engine = remember(puzzle) { MathCrosswordEngine(puzzle) }

    var elapsedMs by remember(puzzle) { mutableStateOf(savedForLevel?.elapsedMs ?: 0L) }
    // First-run tour: the clock waits for it to finish.
    var coachVisible by remember(puzzle) { mutableStateOf(!AppContainer.coachPrefsStore.hasSeenMathCoach()) }
    var running by remember(puzzle) { mutableStateOf(!coachVisible) }
    var gridBounds by remember { mutableStateOf(Rect.Zero) }
    var padBounds by remember { mutableStateOf(Rect.Zero) }
    var backspaceBounds by remember { mutableStateOf(Rect.Zero) }
    var actionBounds by remember { mutableStateOf(Rect.Zero) }
    val dragState = rememberLetterDragState()

    LaunchedEffect(puzzle) {
        savedForLevel?.let { engine.applySnapshot(it.fills, it.hintsUsed, it.mistakes, it.selectedRow, it.selectedCol) }
        val blanks = puzzle.numberCellIndices.count { !puzzle.cells[it].given }
        // A Daily that rolled a math level is still a daily: same event pair as the word Daily.
        AppContainer.analytics.logEvent(
            if (isDaily) AnalyticsEvents.DAILY_PUZZLE_STARTED else AnalyticsEvents.PUZZLE_STARTED,
            mapOf(
                "mode" to MODE, "level_id" to levelId, "tier" to tier.name, "level_index" to index,
                "width" to puzzle.width, "height" to puzzle.height, "blank_cells" to blanks, "resumed" to resumed,
            ),
        )
        if (resumed) {
            AppContainer.analytics.logEvent(AnalyticsEvents.SESSION_RESUMED, mapOf("mode" to MODE, "level_id" to levelId))
        }
        if (coachVisible) {
            AppContainer.analytics.logEvent(AnalyticsEvents.PLAY_COACH_SHOWN, mapOf("mode" to MODE, "level_id" to levelId))
        }
    }

    // The clock stops while the app is backgrounded, as on the crossword, so time spent away
    // never lands in best times or challenge times.
    var paused by remember { mutableStateOf(false) }
    val lifecycleOwner = LocalLifecycleOwner.current
    DisposableEffect(lifecycleOwner) {
        val observer = LifecycleEventObserver { _, event ->
            when (event) {
                Lifecycle.Event.ON_PAUSE, Lifecycle.Event.ON_STOP -> paused = true
                Lifecycle.Event.ON_RESUME -> paused = false
                else -> Unit
            }
        }
        lifecycleOwner.lifecycle.addObserver(observer)
        onDispose { lifecycleOwner.lifecycle.removeObserver(observer) }
    }

    LaunchedEffect(puzzle, running, paused) {
        if (!running || paused) return@LaunchedEffect
        val base = elapsedMs
        val mark = TimeSource.Monotonic.markNow()
        while (running && !engine.isSolved) {
            elapsedMs = base + mark.elapsedNow().inWholeMilliseconds
            delay(200)
        }
    }

    LaunchedEffect(engine.isSolved) {
        if (engine.isSolved) {
            running = false
            val progression = AppContainer.mathProgressionManager
            val hadStarsBefore = progression.starsFor(levelId) > 0
            val nextTier = DifficultyTier.entries.getOrNull(tier.ordinal + 1)
            val tierUnlockedBefore = nextTier?.let { progression.isTierUnlocked(it) } ?: false

            val stars = progression.recordCompletion(levelId, engine.hintsUsed, engine.mistakes, elapsedMs)

            if (!hadStarsBefore && stars >= 1) {
                AppContainer.analytics.logEvent(
                    AnalyticsEvents.LEVEL_UNLOCKED,
                    mapOf("mode" to MODE, "level_id" to levelId, "tier" to tier.name, "stars_earned" to stars),
                )
            }
            if (nextTier != null && !tierUnlockedBefore && progression.isTierUnlocked(nextTier)) {
                AppContainer.analytics.logEvent(
                    AnalyticsEvents.TIER_UNLOCKED,
                    mapOf("mode" to MODE, "tier" to nextTier.name, "unlocked_from_tier" to tier.name),
                )
            }
            AppContainer.analytics.logEvent(
                if (isDaily) AnalyticsEvents.DAILY_PUZZLE_COMPLETED else AnalyticsEvents.PUZZLE_COMPLETED,
                mapOf(
                    "mode" to MODE, "level_id" to levelId, "tier" to tier.name, "stars" to stars,
                    "hints_used" to engine.hintsUsed, "mistakes" to engine.mistakes,
                    "duration_ms" to elapsedMs, "was_resumed" to resumed,
                ),
            )
            AppContainer.mathSessionStore.save(null)
            if (isDaily) AppContainer.dailyPuzzleManager.recordCompletionAndLog(AppContainer.analytics, GameMode.MATH_CROSSWORD)
            AppContainer.playerJourney.onGameCompleted(GameMode.MATH_CROSSWORD, isDaily = isDaily)
            onSolved(levelId, stars, elapsedMs)
        }
    }

    fun persistAndLeave() {
        if (!engine.isSolved) {
            val totalEditable = engine.cells.count { it.editable }
            val filled = engine.cells.count { it.editable && it.userText.isNotEmpty() }
            val percent = if (totalEditable > 0) filled * 100 / totalEditable else 0
            val hasProgress = filled > 0 || engine.hintsUsed > 0
            AppContainer.analytics.logEvent(
                AnalyticsEvents.PUZZLE_ABANDONED,
                mapOf(
                    "mode" to MODE, "level_id" to levelId, "tier" to tier.name, "elapsed_ms" to elapsedMs,
                    "filled_cells" to filled, "total_cells" to totalEditable, "percent_complete" to percent,
                    "hints_used" to engine.hintsUsed, "mistakes" to engine.mistakes,
                ),
            )
            if (hasProgress) {
                AppContainer.mathSessionStore.save(
                    SavedMathSession(
                        levelId = levelId,
                        index = index,
                        puzzle = puzzle,
                        fills = engine.snapshotFills(),
                        hintsUsed = engine.hintsUsed,
                        mistakes = engine.mistakes,
                        elapsedMs = elapsedMs,
                        selectedRow = engine.selected?.row,
                        selectedCol = engine.selected?.col,
                    ),
                )
                AppContainer.analytics.logEvent(
                    AnalyticsEvents.PUZZLE_PROGRESS_SAVED,
                    mapOf("mode" to MODE, "level_id" to levelId, "percent_complete" to percent),
                )
            } else {
                AppContainer.mathSessionStore.save(null)
                AppContainer.analytics.logEvent(
                    AnalyticsEvents.PUZZLE_PROGRESS_DISCARDED,
                    mapOf("mode" to MODE, "level_id" to levelId),
                )
            }
        }
        onBack()
    }

    val finishCoach: (skipped: Boolean, stepIndex: Int) -> Unit = { skipped, stepIndex ->
        AppContainer.coachPrefsStore.markMathCoachSeen()
        AppContainer.analytics.logEvent(
            AnalyticsEvents.PLAY_COACH_FINISHED,
            mapOf("mode" to MODE, "skipped" to skipped, "step" to stepIndex),
        )
        coachVisible = false
        running = true
    }

    BackHandler(enabled = true) { if (coachVisible) finishCoach(true, 0) else persistAndLeave() }

    val focusRequester = remember { FocusRequester() }
    // Keys only reach the board while it holds focus; claim it again once the tour is dismissed.
    LaunchedEffect(puzzle, coachVisible) { if (!coachVisible) runCatching { focusRequester.requestFocus() } }

    val colors = MaterialTheme.game
    val scope = rememberCoroutineScope()
    var revealLoading by remember(puzzle) { mutableStateOf(false) }

    // Free reveals first, then a rewarded ad when one can actually be shown.
    val revealViaAd = mathConfig.revealRequiresAd &&
        engine.hintsUsed >= mathConfig.revealFreePerLevel &&
        AppContainer.adsCoordinator.isRewardedAvailable()

    fun grantReveal() {
        engine.revealSelected()
        AppContainer.analytics.logEvent(
            AnalyticsEvents.REVEAL_LETTER_USED,
            mapOf("mode" to MODE, "level_id" to levelId, "reveals_used_total" to engine.hintsUsed, "revealed_via_ad" to revealViaAd),
        )
    }

    val onReveal: () -> Unit = onReveal@{
        if (revealLoading || engine.selected == null) return@onReveal
        if (!revealViaAd) {
            grantReveal()
            return@onReveal
        }
        AppContainer.analytics.logEvent(
            AnalyticsEvents.AD_REWARDED_REQUESTED,
            mapOf("placement" to AdPlacement.MATH_CROSSWORD.wireName, "level_id" to levelId),
        )
        revealLoading = true
        scope.launch {
            val earned = AppContainer.adsCoordinator.showRewarded(AdPlacement.MATH_CROSSWORD)
            revealLoading = false
            if (earned) grantReveal()
        }
    }

    GameScreen(
        adPlacement = AdPlacement.MATH_CROSSWORD,
        overlay = {
            if (coachVisible) {
                ModeCoachTour(
                    steps = listOf(
                        ModeCoachStep(
                            title = "Make every equation true",
                            body = "Fill the empty squares with numbers so each row and column adds up. Tap a square to select it.",
                            hole = gridBounds,
                        ),
                        ModeCoachStep(
                            title = "Tap or drag a number",
                            body = "Tap a digit to type it into the selected square, or drag it straight onto the square you want.",
                            hole = padBounds,
                        ),
                        ModeCoachStep(
                            title = "Typed a wrong number?",
                            body = "Press backspace to remove it from the selected square, then type the right one. On a keyboard, the Backspace key works too.",
                            hole = backspaceBounds,
                        ),
                        ModeCoachStep(
                            title = "Stuck? Check it",
                            body = "Check marks right and wrong squares. Reveal shows the answer for the selected square.",
                            hole = actionBounds,
                        ),
                    ),
                    onFinish = finishCoach,
                )
            }
        },
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .focusRequester(focusRequester)
                .focusable()
                .onPreviewKeyEvent { event ->
                    if (event.type != KeyEventType.KeyDown || coachVisible) return@onPreviewKeyEvent false
                    val ch = event.utf16CodePoint.toChar()
                    when {
                        ch.isDigit() -> { engine.appendDigit(ch); true }
                        event.key == Key.Backspace || event.key == Key.Delete -> { engine.backspace(); true }
                        event.key == Key.DirectionRight || event.key == Key.Tab -> { engine.moveSelection(true); true }
                        event.key == Key.DirectionLeft -> { engine.moveSelection(false); true }
                        else -> false
                    }
                },
            verticalArrangement = Arrangement.spacedBy(Spacing.md),
        ) {
            GameTopBar(
                title = "Level $index",
                subtitle = tierLabel(tier),
                onBack = { persistAndLeave() },
                trailing = {
                    StatChip(value = formatClock(elapsedMs), tint = colors.gold)
                    if (engine.mistakes > 0) {
                        StatChip(value = engine.mistakes.toString(), label = "errors", tint = colors.danger)
                    }
                },
            )

            MathGridView(
                engine = engine,
                dragState = dragState,
                modifier = Modifier.weight(1f).fillMaxWidth().reportCoachBounds { gridBounds = it },
            )

            Row(
                modifier = Modifier.fillMaxWidth().reportCoachBounds { actionBounds = it },
                horizontalArrangement = Arrangement.spacedBy(Spacing.md),
            ) {
                GameButton(
                    text = "Check",
                    onClick = {
                        engine.check()
                        AppContainer.analytics.logEvent(
                            AnalyticsEvents.GRID_CHECKED,
                            mapOf("mode" to MODE, "level_id" to levelId, "mistakes_total" to engine.mistakes),
                        )
                    },
                    style = GameButtonStyle.SECONDARY,
                    icon = { tint -> IconCheck(size = 18.dp, tint = tint) },
                    fillWidth = true,
                    modifier = Modifier.weight(1f),
                )
                if (mathConfig.showReveal) {
                    GameButton(
                        text = "Reveal",
                        caption = if (revealViaAd) "Ad" else null,
                        onClick = onReveal,
                        enabled = !revealLoading,
                        style = GameButtonStyle.GHOST,
                        icon = { tint -> IconBulb(size = 18.dp, tint = tint) },
                        fillWidth = true,
                        modifier = Modifier.weight(1f),
                    )
                }
            }

            NumberPad(
                onDigit = { engine.appendDigit(it) },
                onBackspace = { engine.backspace() },
                dragState = dragState,
                onDropOnCell = { pos, digit -> engine.placeDigitAt(pos, digit) },
                modifier = Modifier.widthIn(max = 420.dp).align(Alignment.CenterHorizontally)
                    .reportCoachBounds { padBounds = it },
                backspaceModifier = Modifier.reportCoachBounds { backspaceBounds = it },
            )
        }
        LetterDragOverlay(state = dragState, tileSize = 46.dp)
        }
    }
}

private fun formatClock(ms: Long): String {
    val totalSeconds = ms / 1000
    val minutes = totalSeconds / 60
    val seconds = totalSeconds % 60
    return minutes.toString() + ":" + seconds.toString().padStart(2, '0')
}
